import { useState } from 'react';

import { Avatar } from '@/components/ui/Avatar';
import { Dialog } from '@/components/ui/Dialog';
import { Pagination } from '@/components/ui/Pagination';
import { SearchInput } from '@/components/ui/SearchInput';
import { EmptyState, ErrorState, LoadingState } from '@/components/ui/StateMessage';
import { useDebouncedValue } from '@/hooks/useDebouncedValue';
import { fullName } from '@/utils/text';

import { useMembers } from '../../hooks/useMembers';
import type { Member } from '../../schemas/member.schema';
import { MemberStatusBadge } from '../MemberStatusBadge';
import styles from './MemberPickerDialog.module.css';

const RESULTS = 6;

export interface MemberPickerDialogProps {
  open: boolean;
  onClose: () => void;
  onPick: (member: Member) => void;
  title: string;
  description?: string | undefined;
}

/** Search the register and pick one member — e.g. to sell a membership from the dashboard. */
export function MemberPickerDialog({
  open,
  onClose,
  onPick,
  title,
  description,
}: MemberPickerDialogProps) {
  return (
    <Dialog open={open} onClose={onClose} title={title} description={description} size="md">
      <PickerBody onPick={onPick} />
    </Dialog>
  );
}

/** Mounted only while the dialog is open, so every opening starts with an empty search. */
function PickerBody({ onPick }: { onPick: (member: Member) => void }) {
  const [text, setText] = useState('');
  const query = useDebouncedValue(text.trim());
  // The page belongs to the search it was picked for: a new search starts on page 1.
  const [paging, setPaging] = useState({ query: '', page: 1 });
  const pageNumber = paging.query === query ? paging.page : 1;
  const members = useMembers({ page: pageNumber, pageSize: RESULTS, q: query || undefined });
  const page = members.data;

  return (
    <div className={styles.body}>
      <SearchInput
        label="Search members"
        placeholder="Name, mobile or member code"
        value={text}
        onChange={setText}
        loading={text.trim() !== query || members.isFetching}
        autoFocus
      />

      {members.isError ? (
        <ErrorState
          compact
          error={members.error}
          onRetry={() => {
            void members.refetch();
          }}
        />
      ) : page === undefined ? (
        <LoadingState compact />
      ) : page.items.length === 0 ? (
        <EmptyState
          compact
          icon="members"
          title={query ? 'No member matches this search' : 'No members yet'}
        />
      ) : (
        <>
          <ul className={styles.list} aria-busy={members.isPlaceholderData}>
            {page.items.map((member) => {
              const name = fullName(member.firstName, member.lastName);
              return (
                <li key={member.id}>
                  <button
                    type="button"
                    className={styles.row}
                    onClick={() => {
                      onPick(member);
                    }}
                  >
                    <Avatar name={name} size="sm" />
                    <span className={styles.text}>
                      <span className={styles.name}>{name}</span>
                      <span className={styles.meta}>
                        <span className="tabular">{member.memberCode}</span> · {member.mobile}
                      </span>
                    </span>
                    <MemberStatusBadge status={member.status} />
                  </button>
                </li>
              );
            })}
          </ul>
          <Pagination
            meta={page.meta}
            onPageChange={(next) => {
              setPaging({ query, page: next });
            }}
            noun="members"
          />
        </>
      )}
    </div>
  );
}
